import { useEffect, useState } from 'react';
import { Contract, ethers as utils } from 'ethers';
import { INTERVALS, collectIntervalPayments, decodePaymentLogs } from './intervalPayments';
import { readLogRange } from './readLogRange';
import { starTaxContractAddress, starTaxContractABI } from '../contract';

export default function useWalletPayments(wallet, providers, onBalance) {
  const [reward, setReward] = useState(null);
  useEffect(() => {
    setReward(null);
    if (!wallet || !providers.length) {onBalance(0);return undefined;}
    let cancelled = false,busy = false,cursor = null,receivers = {};
    const poll = async () => {
      if (busy || cancelled) return;
      busy = true;
      try {
        let failure;
        for (let attempt = 0; attempt < providers.length; attempt++) {
          const provider = providers[attempt];
          try {
            const st = new Contract(starTaxContractAddress, starTaxContractABI, provider);
            const latest = await provider.getBlockNumber();
            if (cursor === null) {
              const [values, balance] = await Promise.all([
              Promise.all(INTERVALS.map((n) => st['currentReceiver' + n]({ blockTag: latest }))),
              st.balanceOf(wallet, { blockTag: latest })]
              );
              if (cancelled) return;
              receivers = Object.fromEntries(INTERVALS.map((n, i) => [n, values[i]]));
              cursor = latest;
              onBalance(utils.formatUnits(balance, 18));
              return;
            }
            if (latest <= cursor) return;
            const toBlock = Math.min(latest, cursor + 100);
            const raw = await readLogRange({ fromBlock: cursor + 1, toBlock, address: starTaxContractAddress,
              getLogs: (args) => provider.getLogs(args), getBlock: (number) => provider.getBlock(number) });
            const logs = decodePaymentLogs(raw);
            const hashes = [...new Set(logs.filter((x) => x.parsed?.name === 'Transfer' && x.parsed.args[1].toLowerCase() === wallet.toLowerCase()).map((x) => x.transactionHash))];
            const origins = {};
            for (const hash of hashes) {
              const tx = await provider.getTransaction(hash);
              if (!tx) throw new Error('Reward transaction unavailable; retrying this range');
              origins[hash] = tx.from;
            }
            const result = collectIntervalPayments(logs, receivers, origins, wallet);
            const balance = await st.balanceOf(wallet, { blockTag: toBlock });
            if (cancelled) return;
            receivers = result.receivers;
            cursor = toBlock; // advance only after ALL reads succeed
            onBalance(utils.formatUnits(balance, 18));
            if (!(result.total === BigInt(0))) setReward({ wallet, id: result.ids.join('|'), amount: utils.formatUnits(result.total, 18) });
            return;
          } catch (error) {failure = error;}
        }
        if (!cancelled) console.warn('[StarTax wallet] All RPCs failed; keeping the last snapshot and retrying.', failure);
      } finally {busy = false;}
    };
    poll();
    const timer = setInterval(poll, 4000);
    return () => {cancelled = true;clearInterval(timer);};
  }, [wallet, providers, onBalance]);
  return reward;
}
